import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { PaymentReport, PaymentDetailExport } from '../../../../domain/models/payment-report.model';
import { toCsv, downloadCsv } from '../report-export.util';

@Component({
  selector: 'app-detailed-tab',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, DatePipe, MatTableModule, MatButtonModule, MatIconModule,
    FormsModule, MatFormFieldModule, MatSelectModule],
  template: `
    @if (!detail) { <p class="empty">Run a report to list individual payments.</p> }
    @if (detail) {
      @if (detail.capped) {
        <div class="warn">Showing the first {{ detail.cap }} payments. Narrow the date range or scope to see all.</div>
      }
      <div class="actions">
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="type-filter">
          <mat-label>Payment type</mat-label>
          <mat-select [(ngModel)]="typeFilter">
            <mat-option value="">All types</mat-option>
            <mat-option value="WALK_IN">Walk-in</mat-option>
            <mat-option value="MAIL_IN">Mail-in</mat-option>
            <mat-option value="ONLINE">Online</mat-option>
          </mat-select>
        </mat-form-field>
        <span class="count">{{ rows.length }} of {{ detail.payments.length }}</span>
        <button mat-stroked-button (click)="exportCsv()"><mat-icon>table_view</mat-icon> Export CSV</button>
      </div>
      <table mat-table [dataSource]="rows" class="detail-table">
        <ng-container matColumnDef="receiptNumber"><th mat-header-cell *matHeaderCellDef>Receipt</th>
          <td mat-cell *matCellDef="let p">{{ p.receiptNumber }}</td></ng-container>
        <ng-container matColumnDef="paymentDate"><th mat-header-cell *matHeaderCellDef>Date</th>
          <td mat-cell *matCellDef="let p">{{ p.paymentDate | date:'short' }}</td></ng-container>
        <ng-container matColumnDef="accountName"><th mat-header-cell *matHeaderCellDef>Account</th>
          <td mat-cell *matCellDef="let p">{{ p.accountName }}</td></ng-container>
        <ng-container matColumnDef="officeName"><th mat-header-cell *matHeaderCellDef>Office</th>
          <td mat-cell *matCellDef="let p">{{ p.officeName }}</td></ng-container>
        <ng-container matColumnDef="jurisdictions"><th mat-header-cell *matHeaderCellDef>Jurisdiction</th>
          <td mat-cell *matCellDef="let p">{{ p.jurisdictions || '—' }}</td></ng-container>
        <ng-container matColumnDef="paymentType"><th mat-header-cell *matHeaderCellDef>Type</th>
          <td mat-cell *matCellDef="let p">{{ typeLabel(p.paymentType) }}</td></ng-container>
        <ng-container matColumnDef="cashierName"><th mat-header-cell *matHeaderCellDef>Cashier</th>
          <td mat-cell *matCellDef="let p">{{ p.cashierName }}</td></ng-container>
        <ng-container matColumnDef="status"><th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let p">{{ p.status }}</td></ng-container>
        <ng-container matColumnDef="totalPayment"><th mat-header-cell *matHeaderCellDef>Amount</th>
          <td mat-cell *matCellDef="let p">{{ p.totalPayment | currency }}</td></ng-container>
        <tr mat-header-row *matHeaderRowDef="cols"></tr>
        <tr mat-row *matRowDef="let row; columns: cols;"></tr>
      </table>
      @if (rows.length === 0) {
        <p class="empty">No payments of that type in this report.</p>
      }
    }
  `,
  styles: [`
    .empty { color: #64748b; }
    .warn { background: #fff7ed; color: #9a3412; padding: 8px 12px; border-radius: 8px; margin-bottom: 12px; }
    .actions { margin-bottom: 12px; display: flex; align-items: center; gap: 12px; }
    .type-filter { width: 180px; }
    .count { color: #64748b; font-size: 13px; margin-right: auto; }
    .detail-table { width: 100%; }
  `],
})
export class DetailedTabComponent {
  @Input() report: PaymentReport | null = null;
  @Input() detail: PaymentDetailExport | null = null;

  readonly cols = ['receiptNumber', 'paymentDate', 'accountName', 'officeName', 'jurisdictions',
    'paymentType', 'cashierName', 'status', 'totalPayment'];

  /** Filters the loaded rows rather than refetching: the report is already in hand. */
  typeFilter = '';

  get rows() {
    const all = this.detail?.payments ?? [];
    return this.typeFilter ? all.filter(p => p.paymentType === this.typeFilter) : all;
  }

  typeLabel(type: string): string {
    switch (type) {
      case 'WALK_IN': return 'Walk-in';
      case 'MAIL_IN': return 'Mail-in';
      case 'ONLINE': return 'Online';
      default: return type || '—';
    }
  }

  exportCsv(): void {
    if (!this.detail) return;
    // Exports what is on screen, filter included - a CSV that disagreed with the table
    // would be worse than no filter at all.
    const rows = this.rows.map(p => [
      p.receiptNumber, p.paymentDate, p.accountName, p.officeName, p.jurisdictions ?? '',
      this.typeLabel(p.paymentType), p.cashierName, p.status, String(p.totalPayment),
    ]);
    downloadCsv('payment-detail.csv',
      toCsv(['Receipt', 'Date', 'Account', 'Office', 'Jurisdiction', 'Type', 'Cashier', 'Status', 'Amount'], rows));
  }
}
